import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowDown, Coffee, Croissant } from "lucide-react";
import { IMAGES } from "@/data/content";
import { useLenis } from "@/hooks/useLenis";

const EASE = [0.16, 1, 0.3, 1];

const MaskedLine = ({ index, children }) => (
  <span className="block overflow-hidden pb-1">
    <motion.span
      className="block"
      initial={{ y: "115%" }}
      animate={{ y: "0%" }}
      transition={{ duration: 1.05, delay: 0.35 + index * 0.14, ease: EASE }}
    >
      {children}
    </motion.span>
  </span>
);

const openStatus = () => {
  const now = new Date();
  const day = now.getDay();
  const hour = now.getHours();
  const schedule = { 0: [9, 13], 1: null, 2: [8, 19], 3: [8, 19], 4: [8, 19], 5: [8, 19], 6: [9, 19] };
  const today = schedule[day];
  if (!today) return "Fermé le lundi · ouvert demain dès 8 h";
  if (hour >= today[0] && hour < today[1]) return `Ouvert · jusqu'à ${today[1]} h 00`;
  if (hour < today[0]) return `Ouvre aujourd'hui à ${today[0]} h 00`;
  return "Fermé · on se retrouve demain matin";
};

export const Hero = () => {
  const scrollTo = useLenis();
  const { scrollY } = useScroll();
  const imgY = useTransform(scrollY, [0, 700], [0, 90]);
  const fade = useTransform(scrollY, [0, 420], [1, 0.35]);

  return (
    <section
      id="accueil"
      data-testid="hero-section"
      className="relative overflow-hidden bg-[#FAF7F2] pt-[76px]"
    >
      <div
        aria-hidden="true"
        className="pointer-events-none absolute -right-40 top-24 h-[560px] w-[560px] rounded-full bg-[#D4A359]/15 blur-3xl"
      />
      <div
        aria-hidden="true"
        className="pointer-events-none absolute -left-52 bottom-0 h-[420px] w-[420px] rounded-full bg-[#C85A32]/10 blur-3xl"
      />

      <motion.div
        style={{ opacity: fade }}
        className="mx-auto grid max-w-7xl grid-cols-1 items-center gap-14 px-5 pb-20 pt-14 sm:px-8 lg:min-h-[calc(100vh-76px)] lg:grid-cols-12 lg:gap-8 lg:pb-10 lg:pt-8"
      >
        <div className="relative z-10 lg:col-span-7">
          <motion.div
            initial={{ opacity: 0, y: 18 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.15, ease: EASE }}
            className="mb-8 flex flex-wrap items-center gap-4"
          >
            <span
              data-testid="hero-status-pill"
              className="inline-flex items-center gap-2 rounded-full border border-[#1B3B2B]/15 bg-[#FFFDF9] px-4 py-2 font-mono text-[11px] uppercase tracking-[0.18em] text-[#1B3B2B]"
            >
              <span className="relative flex h-2 w-2">
                <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-[#C85A32] opacity-60" />
                <span className="relative inline-flex h-2 w-2 rounded-full bg-[#C85A32]" />
              </span>
              {openStatus()}
            </span>
            <span className="font-mono text-[11px] uppercase tracking-[0.28em] text-[#1B3B2B]/50">
              Café de spécialité — Rennes
            </span>
          </motion.div>

          <h1
            data-testid="hero-title"
            className="font-display text-4xl font-semibold leading-[1.04] tracking-tight text-[#1B3B2B] sm:text-5xl lg:text-6xl"
          >
            <MaskedLine index={0}>Le goût</MaskedLine>
            <MaskedLine index={1}>
              des <em className="font-medium italic text-[#C85A32]">bonnes</em>
            </MaskedLine>
            <MaskedLine index={2}>choses.</MaskedLine>
          </h1>

          <motion.p
            data-testid="hero-subtitle"
            initial={{ opacity: 0, y: 22 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, delay: 0.85, ease: EASE }}
            className="mt-7 max-w-xl text-base font-light leading-relaxed text-[#3D5A4B] sm:text-lg"
          >
            Café de spécialité, pâtisseries artisanales et cuisine de saison,
            sous les arcades de la place des Lices. Ici, tout est fait maison —
            et tout se savoure lentement.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 22 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, delay: 1, ease: EASE }}
            className="mt-10 flex flex-wrap items-center gap-4"
          >
            <button
              type="button"
              data-testid="hero-cta-menu"
              onClick={() => scrollTo("#la-carte")}
              className="group inline-flex items-center gap-3 rounded-full bg-[#C85A32] px-8 py-4 font-mono text-xs font-medium uppercase tracking-[0.2em] text-[#FAF7F2] transition-colors duration-300 hover:bg-[#A84420]"
            >
              Découvrir notre carte
              <ArrowDown
                size={15}
                className="transition-transform duration-300 group-hover:translate-y-0.5"
              />
            </button>
            <button
              type="button"
              data-testid="hero-cta-story"
              onClick={() => scrollTo("#le-cafe")}
              className="inline-flex items-center gap-3 rounded-full border border-[#1B3B2B]/25 px-8 py-4 font-mono text-xs font-medium uppercase tracking-[0.2em] text-[#1B3B2B] transition-colors duration-300 hover:border-[#1B3B2B] hover:bg-[#1B3B2B] hover:text-[#F5F0E6]"
            >
              Notre histoire
            </button>
          </motion.div>
        </div>

        <div className="relative lg:col-span-5">
          <motion.div
            initial={{ clipPath: "inset(100% 0 0 0)" }}
            animate={{ clipPath: "inset(0% 0 0 0)" }}
            transition={{ duration: 1.3, delay: 0.45, ease: EASE }}
            className="relative mx-auto aspect-[4/5] w-full max-w-[440px] overflow-hidden rounded-b-[28px] rounded-t-[999px] shadow-[0_30px_80px_rgba(27,59,43,0.18)]"
            data-testid="hero-image-frame"
          >
            <motion.img
              src={IMAGES.latte}
              alt="Latte art servi au Café des Arcades"
              style={{ y: imgY }}
              initial={{ scale: 1.18 }}
              animate={{ scale: 1.08 }}
              transition={{ duration: 1.6, delay: 0.45, ease: EASE }}
              className="h-[115%] w-full object-cover"
            />
            <div className="pointer-events-none absolute inset-0 rounded-b-[28px] rounded-t-[999px] ring-1 ring-inset ring-[#1B3B2B]/10" />
          </motion.div>

          <motion.div
            data-testid="hero-badge-maison"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: [0, -10, 0] }}
            transition={{
              opacity: { delay: 1.2, duration: 0.7 },
              y: { delay: 1.2, duration: 5.5, repeat: Infinity, ease: "easeInOut" },
            }}
            className="absolute -left-2 top-16 flex items-center gap-3 rounded-2xl border border-[#1B3B2B]/10 bg-[#FFFDF9]/90 px-4 py-3 shadow-[0_14px_40px_rgba(27,59,43,0.12)] backdrop-blur-md sm:-left-8"
          >
            <span className="flex h-9 w-9 items-center justify-center rounded-full bg-[#C85A32]/10 text-[#C85A32]">
              <Croissant size={17} />
            </span>
            <span className="font-mono text-[10px] font-medium uppercase leading-relaxed tracking-[0.18em] text-[#1B3B2B]">
              100 % fait maison
            </span>
          </motion.div>

          <motion.div
            data-testid="hero-badge-torrefaction"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: [0, 10, 0] }}
            transition={{
              opacity: { delay: 1.35, duration: 0.7 },
              y: { delay: 1.35, duration: 6.5, repeat: Infinity, ease: "easeInOut" },
            }}
            className="absolute -right-2 bottom-14 flex items-center gap-3 rounded-2xl border border-[#1B3B2B]/10 bg-[#1B3B2B] px-4 py-3 shadow-[0_14px_40px_rgba(18,40,29,0.35)] sm:-right-6"
          >
            <span className="flex h-9 w-9 items-center justify-center rounded-full bg-[#D4A359]/15 text-[#D4A359]">
              <Coffee size={17} />
            </span>
            <span className="font-mono text-[10px] font-medium uppercase leading-relaxed tracking-[0.18em] text-[#F5F0E6]">
              Torréfaction artisanale
            </span>
          </motion.div>

          <span
            aria-hidden="true"
            className="absolute -right-4 top-1/2 hidden -translate-y-1/2 rotate-90 font-mono text-[10px] uppercase tracking-[0.4em] text-[#1B3B2B]/35 xl:block"
          >
            Place des Lices — Rennes
          </span>
        </div>
      </motion.div>

      <motion.button
        type="button"
        data-testid="hero-scroll-cue"
        onClick={() => scrollTo("#le-cafe")}
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.8, duration: 0.8 }}
        className="absolute bottom-6 left-1/2 hidden -translate-x-1/2 flex-col items-center gap-2 lg:flex"
        aria-label="Faire défiler"
      >
        <span className="font-mono text-[10px] uppercase tracking-[0.35em] text-[#1B3B2B]/45">
          Défiler
        </span>
        <motion.span
          animate={{ scaleY: [0.3, 1, 0.3], opacity: [0.4, 1, 0.4] }}
          transition={{ duration: 2.2, repeat: Infinity, ease: "easeInOut" }}
          className="h-10 w-px origin-top bg-[#C85A32]"
        />
      </motion.button>
    </section>
  );
};
